import axios from 'axios';

import {firestore, functions, auth} from '@src/db';
import {signOut, onAuthStateChanged, signInWithPopup, FacebookAuthProvider, GoogleAuthProvider, OAuthProvider,
  signInWithEmailAndPassword, getIdToken, getIdTokenResult, updatePassword} from 'firebase/auth';
import router from '@router';
import defaults from '@src/router/defaults';
import {FcmEvent} from '@common/FcmEvent';
import {apiErrorCode, UserType} from '@common/status';
import {eventHub} from '@src/eventHub';
import {configLoadedObj} from '@store/modules/config';
import {FcmLocalStorage} from '@utils/FcmLocalStorage';
import {getUserAuth, getCompanyUser} from '@entities/UserEntity';
import {companyAccessProfiles} from '@common/CompanyAccess';
import {companyFollowsMatrizSystemLine} from '@common/CompanySystemLine';

const Workspace = require ('@common/Workspace');

async function getClaims() {
  const idTokenResult = await getIdTokenResult(auth.currentUser);
  return {
    type: {},
    rules: {},
    c: {},
    info: null,
    ...idTokenResult?.claims?.jw,
  };
}

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  currentCompany: getSavedState('auth.currentCompany'),
  currentCompanyId: getSavedState('auth.currentCompanyId'),
  currentCompanyMatrizId: getSavedState('auth.currentCompanyMatrizId'),
  // #1259: `{id, systemLine}` da matriz, só quando a empresa em uso segue a matriz no guard de
  // sistema (filial que não é cooperado cliente). Ver `syncCompanyMatriz`.
  currentCompanyMatriz: getSavedState('auth.currentCompanyMatriz'),
  isSuperAdminAccess: getSavedState('auth.isSuperAdminAccess'),
  currentWorkspace: getSavedState('auth.currentWorkspace'),
  systemNeedReload: false,
};

let unsubscribes = {};
let local= {};

export const userLoadedObj = new FcmEvent(10000);
export function getUserLoaded() {
  return userLoadedObj.getPromise();
}
userLoadedObj.getPromise().then(()=>{
  console.log('UserLoaded Loaded');
});

class ErrorAuth extends Error {
  constructor(message, code) {
    super(message);
    this.message = message;
    this.code = code;
  }
  // this.name = 'MeuErro';
  // this.message = message || 'Mensagem de erro padrão';
  // this.stack = (new Error()).stack;
}

let unsubscribeOnAuthStateChanged = null;
export let ON_LOGIN = false;

function unsubscribeAll() {
  for(const i in unsubscribes) {
    if(typeof unsubscribes[i] === 'function') {
      unsubscribes[i]();
      unsubscribes[i]= null;
    }
  }
  unsubscribes = {};
  local = {};
  clearListenerRetryTimers();
}


function unsubscribe(name) {
  if(typeof unsubscribes[name] === 'function') {
    unsubscribes[name]();
    unsubscribes[name]= null;
  }
}

// Re-tentativa com backoff exponencial para listeners/fluxos derrubados por
// erros transitórios (rede intermitente). Os delays sobrevivem a unsubscribeAll
// para o backoff crescer entre tentativas; são zerados quando o servidor
// confirma dados (resetListenerRetry).
let retryTimers = {};
const retryDelays = {};
const RETRY_BASE_DELAY = 2000;
const RETRY_MAX_DELAY = 60000;

function scheduleListenerRetry(name, fn) {
  if(retryTimers[name]) {
    clearTimeout(retryTimers[name]);
  }
  const delay = retryDelays[name] || RETRY_BASE_DELAY;
  retryDelays[name] = Math.min(delay*2, RETRY_MAX_DELAY);
  console.warn('scheduleListenerRetry', name, 'em '+delay+'ms');
  retryTimers[name] = setTimeout(function() {
    retryTimers[name] = null;
    fn();
  }, delay);
}

function resetListenerRetry(name) {
  delete retryDelays[name];
}

function clearListenerRetryTimers() {
  for(const i in retryTimers) {
    if(retryTimers[i]) {
      clearTimeout(retryTimers[i]);
    }
  }
  retryTimers = {};
}

function snapshotError(name, dispatch, retryFn) {
  return function(error) {
    console.error('snapshotError', name, error);
    // Somente 'permission-denied' indica perda de acesso definitiva.
    // Qualquer outro erro (rede intermitente, token em refresh, etc.) não
    // pode deslogar o usuário — re-assina o listener com backoff.
    if(error?.code==='permission-denied') {
      dispatch('makeLogOut');
      return;
    }
    if(typeof retryFn === 'function') {
      scheduleListenerRetry(name, retryFn);
    }
  };
}

// Sem rede o Firestore entrega snapshot do cache local (fromCache=true); com o
// cache em memória vazio o doc vem "inexistente" mesmo existindo no servidor.
// A ausência do doc só é confiável quando confirmada pelo servidor.
function isOfflineDocMiss(doc) {
  return !doc.data() && doc.metadata.fromCache;
}


export const mutations = {
  SET_CURRENT_WORKSPACE(state, newValue) {
    state.currentWorkspace = newValue;
    saveState('auth.currentWorkspace', newValue);
  },
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = getUserAuth(newValue, state.currentCompanyId, state.currentCompanyMatrizId);
    saveState('auth.currentUser', state.currentUser);
    setDefaultAuthHeaders(state);
  },
  SET_CURRENT_COMPANY(state, newValue) {
    state.currentCompany = newValue || null;
    saveState('auth.currentCompany', newValue);
    state.currentCompanyId = (newValue||{}).id || null;
    if(newValue?.isFilial) {
      state.currentCompanyMatrizId = newValue?.matrizId || null;
    }
    else {
      state.currentCompanyMatrizId = null;
    }
    dropStaleCompanyMatriz(state);
    if (state.currentUser?._companyId!==state.currentCompanyId) {
      state.currentUser = getUserAuth(state.currentUser, state.currentCompanyId, state.currentCompanyMatrizId);
    }
    state.isSuperAdminAccess = false;
    saveState('auth.currentUser', state.currentUser);
    saveState('auth.currentCompanyId', state.currentCompanyId);
    saveState('auth.currentCompanyMatrizId', state.currentCompanyMatrizId);
    saveState('auth.isSuperAdminAccess', state.isSuperAdminAccess);
  },
  SET_CURRENT_COMPANY_SUPER_ADMIN(state, newValue) {
    state.currentCompany = newValue || null;
    saveState('auth.currentCompany', newValue);
    state.currentCompanyId = (newValue||{}).id || null;
    state.isSuperAdminAccess = true;
    if(newValue?.isFilial) {
      state.currentCompanyMatrizId = newValue?.matrizId || null;
    }
    else {
      state.currentCompanyMatrizId = null;
    }
    dropStaleCompanyMatriz(state);
    if (state.currentUser?._companyId!==state.currentCompanyId) {
      state.currentUser = getUserAuth(state.currentUser, state.currentCompanyId, state.currentCompanyMatrizId);
    }
    saveState('auth.currentUser', state.currentUser);
    saveState('auth.currentCompanyMatrizId', state.currentCompanyMatrizId);
    saveState('auth.currentCompanyId', state.currentCompanyId);
    saveState('auth.isSuperAdminAccess', state.isSuperAdminAccess);
  },

  SET_CURRENT(state, {workspace, user, company, companyId, isSuperAdminAccess} = {}) {
    state.currentWorkspace = workspace || null;
    state.currentCompanyId = (company||{}).id || companyId || null;

    state.currentUser = getUserAuth(user, state.currentCompanyId, state.currentCompanyMatrizId);
    state.currentCompany = company || null;
    state.isSuperAdminAccess = isSuperAdminAccess || false;

    if(company?.isFilial) {
      state.currentCompanyMatrizId = company?.matrizId || null;
    }
    else {
      state.currentCompanyMatrizId = null;
    }
    dropStaleCompanyMatriz(state);
    setDefaultAuthHeaders(state);
    saveState('auth.currentCompanyId', state.currentCompanyId);
    saveState('auth.currentCompanyId', state.currentCompanyId);
    saveState('auth.isSuperAdminAccess', state.isSuperAdminAccess);
    saveState('auth.currentWorkspace', workspace || null);
    saveState('auth.currentUser', user || null);
    saveState('auth.currentCompany', company || null);
  },
  SET_SYSTEM_NEED_RELOAD(state) {
    state.systemNeedReload = true;
  },
  SET_CURRENT_COMPANY_MATRIZ(state, newValue) {
    state.currentCompanyMatriz = newValue || null;
    saveState('auth.currentCompanyMatriz', state.currentCompanyMatriz);
  },
};

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    let isValid = false;
    switch(state.currentWorkspace) {
      case Workspace.name.COMPANY:
        if (state.currentUser && state.currentCompany) {
          isValid = true;
        }
        break;
      case Workspace.name.ADMIN:
        if (state.currentUser) {
          isValid = true;
        }
        break;
      case Workspace.name.CLIENT:
        if (state.currentUser) {
          isValid = true;
        }
        break;
      default:
        if(state.currentUser) {
          console.warn('Inconsistencia encontrada, usuário sem workspace definido');
        }
    }
    if(isValid) {
      return state.currentWorkspace;
    }
    return false;
  },
  getterIsResident(state) {
    if (state.isSuperAdminAccess) {
      return false;
    }
    return state.currentUser?._companyUser?.type===UserType.KRESIDENT_ANESTHETIST ||
      state.currentUser?._companyUser?.type===UserType.KRESIDENT_SURGEON ||
      state.currentUser?._companyUser?.profilesIds?.includes('g');
  }
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ commit, state, dispatch }) {
    if(!unsubscribeOnAuthStateChanged) {
      unsubscribeOnAuthStateChanged = onAuthStateChanged(auth, (user) => {
        eventHub.emit('auth:firebaseOnAuthStateChanged', {firebaseUser: user});
        dispatch('onAuthStateChanged', {firebaseUser: user});
      });
    }
    window.addEventListener('storage', function(e) {
      if (event.key==='auth.currentCompanyId' && event.newValue && state.currentCompanyId
        && event.newValue!==state.currentCompanyId) {
        console.log('company changed TODO reload page', state.currentCompanyId, event.newValue);
        commit('SET_SYSTEM_NEED_RELOAD');
        unsubscribeAll();
      }
    });
    setDefaultAuthHeaders(state);
    dispatch('validate');
  },

  /**
   * Útil para alterar a senha do usuário corrente, não usar para logar no sistema
   */
  onlySignInWithEmailAndPassword({}, {email, password}) {
    return signInWithEmailAndPassword(auth, email, password);
  },
  updatePassword({}, password) {
    return updatePassword(auth.currentUser, password);
  },

  async changeAuth({ dispatch }) {
    eventHub.emit('auth:changeAuth', null);
    await dispatch('changeCompany', null, {
      root: false
    });
  },
  async changeCompany({ dispatch }, arg) {
    eventHub.emit('auth:changeCompany', null);
    // await dispatch('caches/changeCompany', null, {
    //   root: true
    // });
  },

  /**
   *
   * @param commit
   * @param dispatch
   * @param getters
   * @param locale
   * @param provider  'password' | 'facebook.com' | 'google.com' | 'apple.com'
   * @param email
   * @param password
   * @param workspace
   * @returns {Promise<never>}
   */
  async logIn({ commit, dispatch, getters },{locale, provider, email, password, workspace=Workspace.name.CLIENT}) {
    // if (getters.loggedIn) return dispatch('validate')
    ON_LOGIN = true;
    let authUser =  false;
    try {
      if(provider==='password') {
        if(!email) {
          return Promise.reject(new ErrorAuth('', 'missingEmail'));
        }
        if(!password) {
          return Promise.reject(new ErrorAuth('', 'missingPassword'));
        }
        authUser = await signInWithEmailAndPassword(auth, email, password);
      }
      else {
        let providerObj = null;
        switch(provider) {
          case 'facebook.com':
            providerObj = new FacebookAuthProvider();
            break;
          case 'google.com':
            providerObj = new GoogleAuthProvider();
            break;
          case 'apple.com':
            providerObj = new OAuthProvider('apple.com');
            break;
          default:
            return Promise.reject(new Error('Unknown provider'));
        }
        if(locale) {
          providerObj.setCustomParameters({
            locale: locale
          });
        }
        authUser = await signInWithPopup(auth, providerObj);
      }
    } catch(error) {
      console.error('Erro em LogIn', error);
      ON_LOGIN = false;
      return Promise.reject(error);
    }
    try {
      await dispatch('clearLogin', {firebaseUser: authUser.user, provider: 'email'});
      await dispatch('onAuthStateChangedLogin', {firebaseUser: authUser.user, provider: provider, isClient: true, workspace: workspace});
    } finally {
      ON_LOGIN = false;
    }
  },

  async logInPhone({ commit, dispatch, getters }, {authUser}) {
    try {
      await dispatch('clearLogin', {firebaseUser: authUser.user, provider: 'email'});
      await dispatch('onAuthStateChangedLogin', {firebaseUser: authUser.user, provider: 'phone', isClient: true});
    } finally {
      ON_LOGIN = false;
    }
  },

  async getAdminMailByFakeMail({commit}, {email}) {
    const userList = await firestore.collection('companiesAdminPublicMirror').where('email', '==', email.toLowerCase()).get();
    if(!userList) {
      return Promise.reject(new Error('Nenhuma empresa encontrada'));
    }
    let fakeMail = false;
    userList.forEach(function(doc) {
      const user = doc.data();
      if(user.fakeMail) {
        fakeMail = user.fakeMail;
      }
    });
    return fakeMail;
  },

  async listCompanies({ commit, dispatch, getters }, { email } = {}) {
    if (getters.loggedIn) return dispatch('validate');

    const userList = await firestore.collection('companiesUsersPublicMirror').where('email', '==', email.toLowerCase()).get();
    if(!userList) {
      return Promise.reject(new Error('Nenhuma empresa encontrada'));
    }

    const promises = [];
    let hasCompany = false;
    const ret = {};

    userList.forEach(function(doc) {
      const user = doc.data();
      if(!user.companyId ||!user.fakeMail) {
        return;
      }
      const companyId = user.companyId;
      const userId = doc.id;

      ret[userId] = {
        company: false,
        fakeMail: user.fakeMail
      };
      promises.push(new Promise(async function(resolve, reject) {
        const companyDoc = await firestore.collection('companies').doc(companyId).get();
        if(companyDoc.exists) {
          const companyTmp = companyDoc.data();
          companyTmp.id = companyDoc.id;
          ret[userId]['company'] = companyTmp;
          hasCompany = true;
        }
        resolve();
      }));
    });

    await Promise.all(promises);
    for(const i in ret) {
      if(!ret[i].company) {
        delete ret[i];
      }
    }

    if(hasCompany) {
      return ret;
    }
    return false;
  },
  async modelCompanyUpdate({ commit, dispatch, getters, state }, {form = {},itemId, isSuperAdmin=false}={}) {
    if(!form || !itemId) {
      return Promise.reject(new Error('id não encontrado'));
    }

    let companyId = itemId;
    let company = null;
    if(isSuperAdmin) {
      const companyObj = await firestore.collection('companies').doc(companyId).get();
      company = Object.assign({}, companyObj.data(), form);
      delete form.companyId;
      form.id = companyId;
    }
    else {
      if(itemId!==state.currentCompany.id) {
        return Promise.reject(new Error('id inválido', itemId, state.currentCompany.id));
      }
      companyId = state.currentCompany.id;
      company = Object.assign({}, state.currentCompany, form);
      delete form.companyId;
      form.id = companyId;
      commit('SET_CURRENT_COMPANY', company);
    }
    await firestore.collection('companies').doc(companyId).set(form,{merge: true});
  },
  async modelCompanyUserUpdate({ commit, dispatch, getters, state }, form = {}) {
    if(!form || !form.id) {
      return Promise.reject(new Error('id não encontrado'));
    }
    if(state.currentUser.id===form.id) { // self update
      const user = Object.assign({}, state.currentUser, form);
      commit('SET_CURRENT_USER', user);
    }
    const userId = form.id;
    delete form.id;
    await firestore.collection('companiesUsers').doc(userId).set(form,{merge: true});
  },
  async modelClientUpdate({ commit, dispatch, getters, state }, form = {}) {
    const userId = state.currentUser.id;
    const user = Object.assign({}, state.currentUser, form);
    delete form.id;
    commit('SET_CURRENT_USER', user);
    await firestore.collection('users').doc(userId).set(form,{merge: true});
  },
  async modelAdminUpdate({ commit, dispatch, getters, state }, form = {}) {
    const userId = state.currentUser.id;
    const user = Object.assign({}, state.currentUser, form);
    delete form.id;
    commit('SET_CURRENT_USER', user);
    await firestore.collection('adminUsers').doc(userId).set(form,{merge: true});
  },

  // async registerClient({ commit, dispatch, getters },
  //   { name,
  //     email,
  //     password
  //   } = {}) {
  //   if (getters.loggedIn) {
  //     return dispatch('validate');
  //   }
  //   ON_LOGIN = true;
  //   let authUser = null;
  //   try {
  //     authUser = await createUserWithEmailAndPassword(auth, email, password);
  //     console.log('authUser', authUser);
  //     await firestore.collection('company_users').doc(authUser.user.uid).set({email: email, name: name||''},{merge: true});
  //     authUser = await signInWithEmailAndPassword(auth, email, password);
  //   } catch(error) {
  //     console.error('Erro em signInWithEmailAndPassword', error);
  //     ON_LOGIN = false;
  //     return Promise.reject(error);
  //   }
  //
  //   await dispatch('clearLogin', {firebaseUser: authUser.user, provider: 'email'});
  //   await dispatch('onAuthStateChangedLogin', {firebaseUser: authUser.user, provider: 'email'});
  //   ON_LOGIN = false;
  //   return 'ok';
  // },

  // async companyRegister({ commit, dispatch, getters },
  //   { name,
  //     email,
  //     password,
  //     companyName
  //   } = {}) {
  //   if (getters.loggedIn) {
  //     return dispatch('validate');
  //   }
  //   ON_LOGIN = true;
  //   const result = await functions.httpsCallable('auth-companyRegister')({name, email, password, companyName});
  //   console.log('result', result);
  //   let authUser = null;
  //
  //   try {
  //     authUser = await signInWithEmailAndPassword(auth, result.data.fakeMail, password);
  //   } catch(error) {
  //     console.error('Erro em signInWithEmailAndPassword', error);
  //     ON_LOGIN = false;
  //     return Promise.reject(error);
  //   }
  //
  //   await dispatch('clearLogin', {firebaseUser: authUser.user, provider: 'email'});
  //   await dispatch('onAuthStateChangedLogin', {firebaseUser: authUser.user, provider: 'email'});
  //   ON_LOGIN = false;
  //   return 'ok';
  // },

  // Logs out the current user.
  logOut({ commit, dispatch }) {
    return dispatch('makeLogOut');

  },

  async authCompanyModulesValidateMultiple({ commit, state, dispatch, rootState }, {companyModules}) {
    await userLoadedObj.getPromise();
    if(state.currentWorkspace!==Workspace.name.ADMIN && companyModules && _.size(companyModules)>0) {
      await configLoadedObj.getPromise();
      // const userProfiles = (state.currentCompanyId?Object.keys(state.currentUser?.companyProfiles?.[state.currentCompanyId]||{}):null) || null;
      // Perfis na empresa em uso pela regra ÚNICA com as claims (@common/CompanyAccess): na filial,
      // os da própria filial + os que a cooperativa concede a ela (`filiaisProfiles[filial]` — antes
      // lido pela chave da cooperativa, e as concessões nunca valiam na tela).
      const userProfiles = companyAccessProfiles(state.currentUser)[state.currentCompanyId] || null;

      const configProfiles = rootState?.config?.configWeb?.companyProfiles || null;
      if(!configProfiles ||!userProfiles) {
        console.error('config.web.companyProfiles não encontrado', configProfiles, userProfiles);
        return false;
      }
      let isValid = false;
      forModules: for(const moduleId in companyModules ||{}) {
        for(const actionId of companyModules[moduleId]||[]) {
          if(configProfiles[moduleId][actionId]?.some((a)=>userProfiles.includes(a))) {
            isValid = true;
            break forModules;
          }
        }
      }
      if(!isValid) {
        return false;
      }
    }
    return true;
  },
  async authCompanyModulesValidate({ commit, state, dispatch, rootState }, {module, action}) {
    await userLoadedObj.getPromise();
    if(state.currentWorkspace!==Workspace.name.ADMIN && module && action) {
      await configLoadedObj.getPromise();
      // const userProfiles = (state.currentCompanyId?Object.keys(state.currentUser?.companyProfiles?.[state.currentCompanyId]||{}):null) || null;
      // Perfis na empresa em uso pela regra ÚNICA com as claims (@common/CompanyAccess): na filial,
      // os da própria filial + os que a cooperativa concede a ela (`filiaisProfiles[filial]` — antes
      // lido pela chave da cooperativa, e as concessões nunca valiam na tela).
      const userProfiles = companyAccessProfiles(state.currentUser)[state.currentCompanyId] || null;
      const configProfiles = rootState?.config?.configWeb?.companyProfiles || null;
      if(!configProfiles ||!userProfiles) {
        console.error('config.web.companyProfiles não encontrado', configProfiles, userProfiles);
        return false;
      }
      return !!configProfiles[module]?.[action]?.some((a) => userProfiles.includes(a));
    }
    if(!module || !action) {
      console.error('module ou action não encontrado', module, action);
    }
    return true;
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  async validate({ commit, state, dispatch, rootState }, {workspaces, rules, companyModules}= {}) {
    await userLoadedObj.getPromise();
    // ADMIN tem acesso ao workspace COMPANY
    if(state.currentWorkspace!==Workspace.name.ADMIN &&
      workspaces?.length>0 && !workspaces?.includes?.(state.currentWorkspace)) {
      console.log('workspace', workspaces);
      console.log('state.currentWorkspace', state.currentWorkspace);
      console.log('state.currentUser', state.currentUser);
      await dispatch('makeLogOut');
      return {isOk: false, error: 'notLogged'};
    }

    let isValid = false;
    switch(state.currentWorkspace) {
      case Workspace.name.COMPANY:
        if (state.currentUser && state.currentCompany) {
          isValid = true;
        }
        break;
      case Workspace.name.ADMIN:
        if (state.currentUser) {
          isValid = true;
        }
        break;
      case Workspace.name.CLIENT:
        if (state.currentUser) {
          isValid = true;
        }
        break;
      default:
        if(state.currentUser) {
          console.warn('Inconsistencia encontrada, usuário sem workspace definido');
        }
    }

    if (!isValid) {
      await dispatch('makeLogOut');
      return {isOk: false, error: 'notLogged'};
    }
    if(state.currentWorkspace!==Workspace.name.ADMIN && rules && rules.length>0) {
      let isValid = false;
      if (state.currentUser.rules) {
        for (const r of rules) {
          if (state.currentUser.rules[r]) {
            isValid = true;
            break;
          }
        }
      }
      if(!isValid) {
        return {isOk: false, error: 'denied'};
      }
    }

    if(state.currentWorkspace!==Workspace.name.ADMIN && companyModules && _.size(companyModules)>0) {
      await configLoadedObj.getPromise();
      // Mesma regra das checagens de módulo (@common/CompanyAccess); antes, na filial, lia
      // `filiaisProfiles` da entrada da própria filial, que não tem o campo.
      const userProfiles = companyAccessProfiles(state.currentUser)[state.currentCompanyId] || null;
      const configProfiles = rootState?.config?.configWeb?.companyProfiles || null;
      if(!configProfiles ||!userProfiles) {
        console.error('config.web.companyProfiles não encontrado', configProfiles, userProfiles);
        return {isOk: false, error: 'denied'};
      }
      let isValid = false;
      forModules: for(const moduleId in companyModules ||{}) {
        for(const actionId of companyModules[moduleId]||[]) {
          if(configProfiles[moduleId][actionId]?.some((a)=>userProfiles.includes(a))) {
            isValid = true;
            break forModules;
          }
        }
      }
      if(!isValid) {
        return {isOk: false, error: 'denied'};
      }
    }

    // TODO Ao acessar uma URL inválida, o usuário é desconectado
    /*    if(rules && rules.length>0) {
      isValid = false;
      if(!state.currentUser.rules) {
        console.warn('Acesso requerido');
        router.push({name: defaults.routeLogged[workspace] || 'login'});
        return false;
      }
      for(let r of rules) {
        if(state.currentUser.rules[r]) {
          isValid = true;
          break;
        }
      }
      if (!isValid){
        console.warn('Acesso requerido');
        router.push({name: defaults.routeLogged[workspace] || 'login'})
        return false;
      }
    } */


    return {isOk: true, user: state.currentUser, currentCompanyId: state.currentCompanyId, error: ''};
  },

  async onAuthStateChangedLogin({ commit, getters, state, dispatch }, {firebaseUser, provider, isClient}) {
    if (firebaseUser && auth.currentUser) {
      let workspace = state.currentWorkspace;

      if(!workspace) {
        const cl = await getClaims();
        if(cl.type.cli) {
          workspace = Workspace.name.CLIENT;
        }
        else if(cl.type?.adm) {
          workspace = Workspace.name.ADMIN;
        }
        else if(cl.type?.comp) {
          workspace = Workspace.name.COMPANY;
        }
        else {
          console.warn('idTokenResult', cl);
        }
      }

      if(!workspace) {
        console.log('buscar workspace');
        const result = await functions.httpsCallable('auth-checkWorkspaceNew')({id: firebaseUser.uid});
        const cl = result.data.data;
        if(result.data && result.data.errorCode===apiErrorCode.OK && cl) {
          if(cl.type?.cli) {
            workspace = Workspace.name.CLIENT;
          }
          else if(cl.type?.adm) {
            workspace = Workspace.name.ADMIN;
          }
          else if(cl.type?.comp) {
            workspace = Workspace.name.COMPANY;
          }
          else {
            console.warn('auth-checkWorkspaceNew', result);
          }
          await getIdToken(auth.currentUser, true);
        }
        else {
          console.error('not workspace', firebaseUser);
          return dispatch('makeLogOut');
        }
      }

      switch(workspace) {
        case Workspace.name.COMPANY:
          return dispatch('loadUserCompany', firebaseUser.uid);
        case Workspace.name.ADMIN:
          const promisesAdmin = [];
          promisesAdmin.push(dispatch('loadUserAdmin', firebaseUser.uid));
          if(state.companyId) {
            promisesAdmin.push(dispatch('loadCompanyOnAdmin', state.companyId));
          }
          return Promise.all(promisesAdmin);
        case Workspace.name.CLIENT:
          return dispatch('loadUserClient', firebaseUser.uid);
      }

      // if(isClient) {
      //   console.warn('ERRO ao procurar workspace do usuário, criando client: '+workspace, firebaseUser)
      //   await firestore.collection('company_users').doc(firebaseUser.uid).set({
      //     createdEnv: 'WEB',
      //     createdType: provider || 'unknown'
      //   }, {merge: true})
      //
      //   let result = await functions.httpsCallable('auth-checkWorkspace')({uid: firebaseUser.uid})
      //   if(result.data && result.data.errorCode===apiErrorCode.OK && result.data.data && result.data.data.type) {
      //     if(result.data.data.type.cli) {
      //       workspace = Workspace.name.CLIENT
      //     }
      //     else if(result.data.data.type.adm) {
      //       workspace = Workspace.name.ADMIN
      //     }
      //     else if(result.data.data.type.comp) {
      //       workspace = Workspace.name.COMPANY
      //     }
      //     else {
      //       console.warn('Erro ao buscar workspace, result', result);
      //     }
      //     await auth.currentUser.getIdToken(true);
      //   }
      //   if(workspace) {
      //     await dispatch('loadUserClient', firebaseUser.uid);
      //     return dispatch('changeAuth');
      //   }
      // }
      console.error('ERRO ao procurar workspace do usuário: '+workspace, firebaseUser);
      return dispatch('makeLogOut');
    } else {
      console.error('not firebaseUser', firebaseUser);
      return dispatch('makeLogOut');
    }
  },

  async onAuthStateChanged({ commit, getters, state, dispatch }, {firebaseUser}) {
    if(ON_LOGIN) {
      return;
    }

    if (firebaseUser && auth.currentUser) {
      let workspace = state.currentWorkspace;

      try {
        if(!workspace) {
          const cl = await getClaims();
          if(cl.type.cli) {
            workspace = Workspace.name.CLIENT;
          }
          else if(cl.type.adm) {
            workspace = Workspace.name.ADMIN;
          }
          else if(cl.type.comp) {
            workspace = Workspace.name.COMPANY;
          }
          else {
            console.warn('idTokenResult', cl);
          }
        }

        if(!workspace) {
          console.warn('buscar workspace');
          const result = await functions.httpsCallable('auth-checkWorkspaceNew')({id: firebaseUser.uid});
          if(result.data && result.data.errorCode===apiErrorCode.OK && result.data.data && result.data.data.type) {
            if(result.data.data.type.cli) {
              workspace = Workspace.name.CLIENT;
            }
            else if(result.data.data.type.adm) {
              workspace = Workspace.name.ADMIN;
            }
            else if(result.data.data.type.comp) {
              workspace = Workspace.name.COMPANY;
            }
            else {
              console.warn('Erro ao buscar workspace, result', result);
            }
            await getIdToken(auth.currentUser, true);
          }
        }
      } catch(error) {
        // Falha ao consultar claims/workspace (tipicamente rede intermitente):
        // não desloga — mantém a sessão e re-tenta com backoff
        console.error('onAuthStateChanged: erro ao buscar workspace, mantendo sessão para retry', error);
        scheduleListenerRetry('onAuthStateChanged', function() {
          dispatch('onAuthStateChanged', {firebaseUser});
        });
        return;
      }
      resetListenerRetry('onAuthStateChanged');

      let loaded = false;
      switch(workspace) {
        case Workspace.name.COMPANY:
          loaded = true;
          await dispatch('loadUserCompany', firebaseUser.uid);
          break;
        case Workspace.name.ADMIN:
          loaded = true;
          await dispatch('loadUserAdmin', firebaseUser.uid);
          break;
        case Workspace.name.CLIENT:
          loaded = true;
          await dispatch('loadUserClient', firebaseUser.uid);
          break;
      }

      if(!loaded) {
        console.error('ERRO ao procurar workspace do usuário', firebaseUser);
        return dispatch('makeLogOut');
      }
      return dispatch('changeAuth');
    } else {
      return dispatch('makeLogOut');
    }
  },

  // loadUserCompany({ commit, getters, state, dispatch }, userId) {
  //   return new Promise(function(resolve, reject) {
  //     if(local.companyUserId && local.companyUserId===userId) {
  //       console.log('loadUserCompany SKIPPED');
  //       return resolve();
  //     }
  //     unsubscribeAll();
  //     local.companyUserId = userId;
  //     try {
  //       unsubscribes.companyUser= firestore.collection('companiesUsers').doc(userId)
  //         .onSnapshot(function(userDoc) {
  //           const userData = userDoc.data();
  //           if(!userData || !userData.companyId) {
  //             dispatch('makeLogOut');
  //             console.error('Usuário sem companyId ou userData userId: '+userId);
  //             return reject(new Error('Usuário sem companyId ou userData userId: '+userId));
  //           }
  //           userData.id = userId;
  //           local.companyId = local.companyUserId;
  //           unsubscribes.company= firestore.collection('companies').doc(userData.companyId)
  //             .onSnapshot(function(companyDoc) {
  //               const companyData = companyDoc.data();
  //               if(!companyData) {
  //                 dispatch('makeLogOut');
  //                 userLoadedObj.reject();
  //                 console.error('company não encontrada userId: '+userId + ' companyId:'+userData.companyId);
  //                 return reject(new Error('company não encontrada userId: '+userId + ' companyId:'+userData.companyId));
  //               }
  //               companyData.id = companyDoc.id;
  //               commit('SET_CURRENT', {
  //                 workspace: Workspace.name.COMPANY,
  //                 user: userData,
  //                 company: companyData
  //               });
  //               userLoadedObj.resolve();
  //               resolve();
  //             }, snapshotError('companies1', dispatch));
  //         }, snapshotError('companiesUsers2', dispatch));
  //     } catch(err) {
  //       console.error('Erro onSnapshot companiesUsers', err);
  //     }
  //   });
  // },
  async loadCompanyOnClient({ commit, getters, state, dispatch }, companyId) {
    return new Promise(function(resolve, reject) {
      if(unsubscribes.company && local.companyId && local.companyId===companyId) {
        console.log('loadCompanyOnClient SKIPPED');
        return resolve();
      }
      unsubscribe('company');
      if(!companyId) {
        commit('SET_CURRENT_COMPANY', null);
        dispatch('syncCompanyMatriz');
        dispatch('changeCompany');
        resolve();
      }
      local.companyId = companyId;
      try {
        unsubscribes.company= firestore.collection('companies').doc(companyId)
          .onSnapshot(function(companyDoc) {
            if(isOfflineDocMiss(companyDoc)) {
              console.warn('loadCompanyOnClient: cache local sem dados da company (offline?), aguardando servidor. companyId: '+companyId);
              return;
            }
            if(!companyDoc.metadata.fromCache) {
              resetListenerRetry('companies3');
            }
            const companyData = companyDoc.data();
            if(!companyData) {
              dispatch('makeLogOut');
              console.error('company não encontrada companyId:'+companyId);
              return reject(new Error('company não encontrada companyId:'+companyId));
            }
            companyData.id = companyDoc.id;
            commit('SET_CURRENT_COMPANY', companyData);
            dispatch('syncCompanyMatriz');
            dispatch('changeCompany');
            resolve();
          }, snapshotError('companies3', dispatch, function() {
            local.companyId = null;
            dispatch('loadCompanyOnClient', companyId);
          }));
      } catch(err) {
        console.error('Erro onSnapshot companies', err);
      }
    });
  },
  async loadCompanyOnAdmin({ commit, getters, state, dispatch }, companyId) {
    return new Promise(function(resolve, reject) {
      if(unsubscribes.company && local.companyId && local.companyId===companyId) {
        console.log('loadCompanyOnAdmin SKIPPED');
        return resolve();
      }
      unsubscribe('company');
      if(!companyId) {
        commit('SET_CURRENT_COMPANY_SUPER_ADMIN', null);
        dispatch('syncCompanyMatriz');
        dispatch('changeCompany');
        resolve();
      }
      local.companyId = companyId;
      try {
        unsubscribes.company= firestore.collection('companies').doc(companyId)
          .onSnapshot(function(companyDoc) {
            if(isOfflineDocMiss(companyDoc)) {
              console.warn('loadCompanyOnAdmin: cache local sem dados da company (offline?), aguardando servidor. companyId: '+companyId);
              return;
            }
            if(!companyDoc.metadata.fromCache) {
              resetListenerRetry('companies4');
            }
            const companyData = companyDoc.data();
            if(!companyData) {
              dispatch('makeLogOut');
              console.error('company não encontrada companyId:'+companyId);
              return reject(new Error('company não encontrada companyId:'+companyId));
            }
            companyData.id = companyId;
            commit('SET_CURRENT_COMPANY_SUPER_ADMIN', companyData);
            dispatch('syncCompanyMatriz');
            dispatch('changeCompany');
            resolve();
          }, snapshotError('companies4', dispatch, function() {
            local.companyId = null;
            dispatch('loadCompanyOnAdmin', companyId);
          }));
      } catch(err) {
        console.error('Erro onSnapshot companies!', err);
      }
    });
  },
  /**
   * #1259: filial que não é cooperado cliente segue o `systemLine` da matriz no guard de sistema.
   * Assina o doc da matriz enquanto a empresa em uso for uma dessas e guarda só `{id, systemLine}`
   * em `currentCompanyMatriz`. Chamado depois de cada troca/atualização de `currentCompany`.
   */
  syncCompanyMatriz({commit, state}) {
    const company = state.currentCompany;
    const matrizId = companyFollowsMatrizSystemLine(company) ? company.matrizId : null;
    if(!matrizId) {
      unsubscribe('companyMatriz');
      local.companyMatrizId = null;
      if(state.currentCompanyMatriz) {
        commit('SET_CURRENT_COMPANY_MATRIZ', null);
      }
      return;
    }
    if(unsubscribes.companyMatriz && local.companyMatrizId===matrizId) {
      return;
    }
    unsubscribe('companyMatriz');
    local.companyMatrizId = matrizId;
    try {
      unsubscribes.companyMatriz = firestore.collection('companies').doc(matrizId)
        .onSnapshot(function(matrizDoc) {
          if(isOfflineDocMiss(matrizDoc)) {
            return;
          }
          const matrizData = matrizDoc.data();
          commit('SET_CURRENT_COMPANY_MATRIZ', matrizData ? {id: matrizDoc.id, systemLine: matrizData.systemLine || null} : null);
        }, function(error) {
          // Sem o snapshotError de propósito: ele desloga em permission-denied, e não conseguir ler a
          // matriz não pode derrubar a sessão. O último valor conhecido fica valendo, e a próxima
          // atualização da empresa tenta de novo.
          console.error('syncCompanyMatriz: erro ao ler a matriz '+matrizId, error);
          unsubscribes.companyMatriz = null;
          local.companyMatrizId = null;
        });
    } catch(err) {
      console.error('Erro onSnapshot companies (matriz)', err);
    }
  },

  loadUserClient({ commit, getters, state, dispatch }, userId) {
    return new Promise(function(resolve, reject) {
      if(userId===local.unsubscribeUserClientId) {
        return resolve();
      }
      unsubscribeAll();
      local.unsubscribeUserClientId = userId;
      try {
        unsubscribes.client = firestore.collection('users').doc(userId)
          .onSnapshot(function(userDoc) {
            if(isOfflineDocMiss(userDoc)) {
              console.warn('loadUserClient: cache local sem dados do usuário (offline?), aguardando servidor. userId: '+userId);
              return;
            }
            if(!userDoc.metadata.fromCache) {
              resetListenerRetry('users5');
            }
            const userData = userDoc.data();
            if(!userData) {
              dispatch('makeLogOut');
              userLoadedObj.reject();
              console.error('Usuário sem userData userId: '+userId);
              eventHub.emit('auth:changeUser', {userId: null, user: null});
              return reject(new Error('Usuário sem userData userId: '+userId));
            }
            userData.id = userId;
            commit('SET_CURRENT_WORKSPACE', Workspace.name.CLIENT);
            // TODO USER
            if(!_.isEqual(userData?.companiesUser, state?.currentUser?.companiesUser)) {
              auth.currentUser?getIdToken(auth.currentUser, true).catch((err)=>{
                console.warn('loadUserClient: falha ao atualizar token (offline?)', err);
              }):'';
            }
            commit('SET_CURRENT_USER', userData);
            userLoadedObj.resolve();
            console.log('TODO, validar se o usuário atual tem acesso a empresa selecionada', state);

            if(state.currentCompanyId) {
              const compU = getCompanyUser(userData, state.currentCompanyId);
              const compUMatriz = getCompanyUser(userData, state.currentCompanyMatrizId);
              if (state.currentCompanyMatrizId) {
                if ((compUMatriz?.active || compUMatriz?.hasAccessFiliais) || (compU?.active && compU?.hasAccess)) {
                  dispatch('loadCompanyOnClient', state.currentCompanyId);
                }
                else {
                  commit('SET_CURRENT_COMPANY', null);
                  dispatch('syncCompanyMatriz');
                }
              }
              else {
                if (compU?.active && compU?.hasAccess) {
                  dispatch('loadCompanyOnClient', state.currentCompanyId);
                }
                else {
                  commit('SET_CURRENT_COMPANY', null);
                  dispatch('syncCompanyMatriz');
                }
              }
            }
            else {
              commit('SET_CURRENT_COMPANY', null);
              dispatch('syncCompanyMatriz');
            }
            resolve();
            eventHub.emit('auth:changeUser', {userId: userId, user: userData});
          }, snapshotError('users5', dispatch, function() {
            local.unsubscribeUserClientId = null;
            dispatch('loadUserClient', userId);
          }));
      } catch(err) {
        eventHub.emit('auth:changeUser', {userId: null, user: null});
        console.error('Erro onSnapshot users', err);
      }
    });
  },
  loadUserAdmin({ commit, getters, state, dispatch }, userId) {
    return new Promise(function(resolve, reject) {
      if(userId===local.unsubscribeUserAdminId) {
        return resolve();
      }
      unsubscribeAll();
      local.unsubscribeUserAdminId = userId;
      try {
        unsubscribes.userAdmin = firestore.collection('adminUsers').doc(userId)
          .onSnapshot(function(userDoc) {
            if(isOfflineDocMiss(userDoc)) {
              console.warn('loadUserAdmin: cache local sem dados do usuário (offline?), aguardando servidor. userId: '+userId);
              return;
            }
            if(!userDoc.metadata.fromCache) {
              resetListenerRetry('adminUsers6');
            }
            const userData = userDoc.data();
            if(!userData) {
              dispatch('makeLogOut');
              console.error('Usuário sem userData userId: '+userId);
              eventHub.emit('auth:changeUser', {userId: null, user: null});
              return reject(new Error('Usuário sem userData userId: '+userId));
            }
            userData.id = userId;
            commit('SET_CURRENT_WORKSPACE', Workspace.name.ADMIN);
            commit('SET_CURRENT_USER', userData);
            userLoadedObj.resolve();
            resolve();
            eventHub.emit('auth:changeUser', {userId: userId, user: userData, isSuperAdminAccess: true});
          }, snapshotError('adminUsers6', dispatch, function() {
            local.unsubscribeUserAdminId = null;
            dispatch('loadUserAdmin', userId);
          }));
        if(state.isSuperAdminAccess && state.currentCompanyId) {
          dispatch('loadCompanyOnAdmin', state.currentCompanyId);
        }
      } catch(err) {
        console.error('Erro onSnapshot adminUsers', err);
        eventHub.emit('auth:changeUser', {userId: null, user: null});
      }
    });
  },

  async makeLogOut({commit, dispatch}, {disableRedirect} = {}) {
    unsubscribeAll();
    commit('SET_CURRENT');
    eventHub.emit('auth:changeUser', {userId: null, user: null});
    try {
      await signOut(auth);
    } catch(error) {
      // An error happened.
      console.error('Error on logout', error);
    }
    if(router && router.currentRoute && router.currentRoute.meta && router.currentRoute.meta.authRequired) {
      const workspace = router.currentRoute.meta.workspace;
      console.log(defaults.routeNotLogged[workspace] || 'login');
      await router.push({name: defaults.routeNotLogged[workspace] || 'login'});
    }
    await dispatch('clearLogin');
    await dispatch('changeAuth');
  },

  async modelOngUpdate({ commit, dispatch, getters, state }, {form = {},itemId}={}) {
    if(!form || !itemId) {
      return Promise.reject(new Error('id não encontrado'));
    }

    if(itemId!==state.currentOng.id) {
      return Promise.reject(new Error('id inválido', itemId, state.currentOng.id));
    }

    const ongId = state.currentOng.id;
    const ong = Object.assign({}, state.currentOng, form);

    delete form.ongId;
    form.id = ongId;
    commit('SET_CURRENT_ONG', ong);
    await firestore.collection('ongs').doc(ongId).set(form,{merge: true});
  },
  async modelOngUserUpdate({ commit, dispatch, getters, state }, form = {}) {
    if(!form || !form.id) {
      return Promise.reject(new Error('id não encontrado'));
    }
    if(state.currentUser.id===form.id) { // self update
      const user = Object.assign({}, state.currentUser, form);
      commit('SET_CURRENT_USER', user);
    }
    const userId = form.id;
    delete form.id;
    await firestore.collection('ongsUsers').doc(userId).set(form,{merge: true});
  },
  async ongRegister({ commit, dispatch, getters },
    { name,
      email,
      password,
      ongName
    } = {}) {
    if (getters.loggedIn) {
      return dispatch('validate');
    }
    ON_LOGIN = true;
    const result = await functions.httpsCallable('auth-ongRegister')({name, email, password, ongName});
    console.log('result', result);
    let authUser = null;

    try {
      authUser = await signInWithEmailAndPassword(auth, result.data.fakeMail, password);
    } catch(error) {
      console.error('Erro em signInWithEmailAndPassword', error);
      ON_LOGIN = false;
      return Promise.reject(error);
    }
    try {
      await dispatch('clearLogin', {firebaseUser: authUser.user, provider: 'email'});
      await dispatch('onAuthStateChangedLogin', {firebaseUser: authUser.user, provider: 'email'});
    } finally {
      ON_LOGIN = false;
    }
    return 'ok';
  },
  async clearLogin() {
    try {
      // Filtros salvos das listagens (jwSearch.*) — alguns guardam nomes de envolvidos.
      FcmLocalStorage.removeByPrefix('jwSearch');
    } catch (err) {
      console.error('Navegador obsoleto ou com os cookies desabilitados, atualize o navegador e ou habilite os cookies', err.message);
    }
  }
};

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(FcmLocalStorage.getItem(key));
}

function saveState(key, state) {
  FcmLocalStorage.setItem(key, JSON.stringify(state, function(k, v) {
    if(typeof v ==='object' && v && (v.wa==='FieldValue.serverTimestamp' || v.fa==='FieldValue.serverTimestamp') || k==='updatedAt') {
      return null;
    }
    return v;
  }));
}

// function deleteState(key) {
//   FcmLocalStorage.removeItem(key);
// }

/**
 * #1259: descarta a matriz guardada quando ela não é mais a matriz da empresa em uso — o
 * `systemLine` de uma empresa anterior não pode decidir o guard de sistema da próxima.
 */
function dropStaleCompanyMatriz(state) {
  if(state.currentCompanyMatriz && state.currentCompanyMatriz.id!==state.currentCompanyMatrizId) {
    state.currentCompanyMatriz = null;
    saveState('auth.currentCompanyMatriz', null);
  }
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser
    ? state.currentUser.token
    : '';
}

